import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, Megaphone } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import LeadFlowHero from "@/components/LeadFlowHero";
import ServiceCards from "@/components/ServiceCards";
import FlowSection from "@/components/FlowSection";
import ProjectShowcase from "@/components/ProjectShowcase";
import AiChatMockup from "@/components/AiChatMockup";
import CrmMockup from "@/components/CrmMockup";
import AiCrmSection from "@/components/AiCrmSection";
import EcosystemGraph from "@/components/EcosystemGraph";
import UseCases from "@/components/UseCases";
import Packages from "@/components/Packages";
import ContactForm from "@/components/ContactForm";
import PhotoBand from "@/components/PhotoBand";

function renderLine(line) {
  return line.split("B2B").map((part, i, arr) => (
    <span key={i}>
      {part}
      {i < arr.length - 1 && <span className="text-gradient">B2B</span>}
    </span>
  ));
}

function Hero() {
  const { t, link } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const lines = t("hero.lines");
  const reduce = useReducedMotion();

  return (
    <section ref={ref} data-testid="hero" className="relative overflow-hidden bg-ink pt-[130px] pb-20 lg:pt-[170px] lg:pb-28">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0A1B32] via-ink to-[#15315C]" />
      <div className="absolute inset-0 opacity-25 [mask-image:linear-gradient(to_left,black_20%,transparent_80%)]">
        <img src="/images/data-flow.jpg" alt="" aria-hidden="true" className="h-full w-full object-cover" />
      </div>
      <motion.div
        aria-hidden="true"
        animate={reduce ? undefined : { x: [0, 70, 0], y: [0, -45, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute -top-40 right-[5%] h-[540px] w-[540px] rounded-full bg-dblue/25 blur-[130px]"
      />
      <motion.div
        aria-hidden="true"
        animate={reduce ? undefined : { x: [0, -55, 0], y: [0, 55, 0] }}
        transition={{ duration: 24, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute -bottom-32 -left-32 h-[460px] w-[460px] rounded-full bg-electric/15 blur-[120px]"
      />
      <div className="absolute inset-0 bg-grid-dark" />
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-ink/80 to-transparent" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid items-center gap-16 lg:grid-cols-2">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-electric/30 bg-electric/10 px-4 py-1.5 text-[11px] font-bold tracking-[0.25em] text-electric"
          >
            {t("hero.label")}
          </motion.p>
          <h1 className="mt-7 font-display text-4xl sm:text-5xl lg:text-[3.6rem] font-extrabold tracking-tight leading-[1.08]">
            {lines.map((l, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                >
                  {renderLine(l)}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-steel leading-relaxed"
          >
            {t("hero.sub")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to={link("/contact")}
              data-testid="hero-cta-primary"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-dblue to-electric px-7 py-4 text-sm font-semibold transition-transform duration-200 hover:scale-[1.04] hover:shadow-[0_0_30px_rgba(33,167,255,0.45)]"
            >
              {t("hero.cta1")} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to={link("/solutions")}
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-4 text-sm font-semibold text-white/85 transition-colors hover:border-electric/50 hover:text-white"
            >
              {t("hero.cta2")}
            </Link>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-9 text-[11px] font-bold tracking-[0.3em] text-steel/70"
          >
            {t("hero.strip")}
          </motion.p>
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          style={{ y }}
          className="hidden sm:block"
        >
          <div className="rounded-3xl border border-white/10 bg-ink2/60 p-8 backdrop-blur">
            <LeadFlowHero />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function SectionCta({ to, children }) {
  return (
    <Link to={to} className="mt-8 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-dblue to-electric px-7 py-3.5 text-sm font-semibold text-white transition-transform duration-200 hover:scale-[1.04]">
      {children} <ArrowRight className="h-4 w-4" />
    </Link>
  );
}

function AdsSection() {
  const { t, link } = useLang();
  return (
    <section data-testid="ads-section" className="bg-ink py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid items-center gap-14 lg:grid-cols-2">
        <Reveal>
          <p className="text-xs font-semibold tracking-[0.25em] text-electric">{t("ads.eyebrow")}</p>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">{t("ads.title")}</h2>
          <p className="mt-5 text-base text-steel leading-relaxed">{t("ads.copy")}</p>
          <div className="mt-7 grid grid-cols-2 gap-x-6 gap-y-3">
            {t("ads.bullets").map((b) => (
              <div key={b} className="flex items-center gap-2.5 text-sm text-white/85">
                <Check className="h-4 w-4 shrink-0 text-electric" /> {b}
              </div>
            ))}
          </div>
          <SectionCta to={link("/b2b-advertising")}>{t("ads.cta")}</SectionCta>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="rounded-2xl border border-white/10 bg-ink2 p-6 shadow-2xl">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-dblue to-electric"><Megaphone className="h-4 w-4 text-white" /></span>
                <p className="font-display text-sm font-bold">META ADS — B2B CAMPAIGN</p>
              </div>
              <div className="flex gap-1.5">
                {t("ads.channels").map((ch) => (
                  <span key={ch} className="rounded-full border border-white/10 px-2.5 py-1 text-[10px] text-steel">{ch}</span>
                ))}
              </div>
            </div>
            <div className="mt-5 space-y-4">
              {[86, 64, 92].map((w, i) => (
                <div key={i}>
                  <div className="flex justify-between text-[11px] text-steel mb-1.5">
                    <span>{t("ads.bullets")[i]}</span>
                    <span className="text-electric font-bold">{[52, 31, 18][i]}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/5">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${w}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.1, delay: 0.2 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                      className="h-full rounded-full bg-gradient-to-r from-dblue to-electric"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function WebSection() {
  const { t, link } = useLang();
  return (
    <section data-testid="web-section" className="bg-mist text-ink py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid items-center gap-14 lg:grid-cols-2">
        <Reveal delay={0.15} className="order-2 lg:order-1">
          <div className="grid grid-cols-2 gap-4">
            {["GR", "EN"].map((l, i) => (
              <div key={l} className={`rounded-xl border border-ink/10 bg-white shadow-xl overflow-hidden ${i === 1 ? "mt-8" : ""}`}>
                <div className="flex items-center gap-1.5 border-b border-ink/10 bg-mist px-3 py-2">
                  <span className="h-2 w-2 rounded-full bg-[#FF5F57]" /><span className="h-2 w-2 rounded-full bg-[#FEBC2E]" /><span className="h-2 w-2 rounded-full bg-[#28C840]" />
                  <span className="ml-auto rounded bg-ink px-2 py-0.5 text-[9px] font-bold text-white">{l}</span>
                </div>
                <div className="space-y-2.5 p-4">
                  <div className="h-2.5 w-2/3 rounded bg-ink/80" />
                  <div className="h-1.5 w-full rounded bg-slate-200" />
                  <div className="h-1.5 w-5/6 rounded bg-slate-200" />
                  <div className="h-1.5 w-4/6 rounded bg-slate-200" />
                  <div className="mt-3 h-7 w-24 rounded-full bg-gradient-to-r from-dblue to-electric" />
                </div>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal className="order-1 lg:order-2">
          <p className="text-xs font-semibold tracking-[0.25em] text-dblue">{t("web.eyebrow")}</p>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">{t("web.title")}</h2>
          <p className="mt-5 text-base text-slate-600 leading-relaxed">{t("web.copy")}</p>
          <p className="mt-4 border-l-2 border-dblue pl-4 text-sm font-semibold text-ink italic">{t("web.message")}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {t("web.chips").map((c) => (
              <span key={c} className="rounded-full border border-ink/15 bg-white px-3.5 py-1.5 text-xs font-medium text-slate-600">{c}</span>
            ))}
          </div>
          <SectionCta to={link("/websites")}>{t("web.cta")}</SectionCta>
        </Reveal>
      </div>
    </section>
  );
}

function AiSection() {
  const { t, link } = useLang();
  return (
    <section data-testid="ai-section" className="bg-ink2 bg-grid-dark py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid items-center gap-14 lg:grid-cols-2">
        <Reveal>
          <p className="text-xs font-semibold tracking-[0.25em] text-electric">{t("ai.eyebrow")}</p>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">{t("ai.title")}</h2>
          <p className="mt-3 font-display text-lg text-gradient font-bold">{t("ai.sub")}</p>
          <p className="mt-5 text-base text-steel leading-relaxed">{t("ai.copy")}</p>
          <div className="mt-7 grid grid-cols-2 gap-x-6 gap-y-3">
            {t("ai.bullets").slice(0, 6).map((b) => (
              <div key={b} className="flex items-center gap-2.5 text-sm text-white/85">
                <Check className="h-4 w-4 shrink-0 text-electric" /> {b}
              </div>
            ))}
          </div>
          <SectionCta to={link("/ai-secretary")}>{t("ai.cta")}</SectionCta>
        </Reveal>
        <Reveal delay={0.15}>
          <AiChatMockup />
        </Reveal>
      </div>
    </section>
  );
}

function CrmSection() {
  const { t, link } = useLang();
  return (
    <section data-testid="crm-section" className="bg-white text-ink py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid items-center gap-14 lg:grid-cols-2">
        <Reveal delay={0.15} className="order-2 lg:order-1">
          <CrmMockup />
        </Reveal>
        <Reveal className="order-1 lg:order-2">
          <p className="text-xs font-semibold tracking-[0.25em] text-dblue">{t("crm.eyebrow")}</p>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">{t("crm.title")}</h2>
          <p className="mt-3 text-base font-semibold text-dblue">{t("crm.sub")}</p>
          <p className="mt-5 text-base text-slate-600 leading-relaxed">{t("crm.copy")}</p>
          <SectionCta to={link("/crm")}>{t("crm.cta")}</SectionCta>
        </Reveal>
      </div>
    </section>
  );
}

function WebappsSection() {
  const { t, link } = useLang();
  return (
    <section data-testid="webapps-section" className="bg-mist text-ink py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-14 lg:grid-cols-2 items-center">
          <Reveal>
            <p className="text-xs font-semibold tracking-[0.25em] text-dblue">{t("webapps.eyebrow")}</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">{t("webapps.title")}</h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">{t("webapps.copy")}</p>
            <div className="mt-7 grid grid-cols-2 gap-x-6 gap-y-3">
              {t("webapps.bullets").map((b) => (
                <div key={b} className="flex items-center gap-2.5 text-sm text-slate-700">
                  <Check className="h-4 w-4 shrink-0 text-dblue" /> {b}
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-2xl border border-ink/10 bg-white p-8 shadow-xl">
              <p className="text-[10px] font-bold tracking-[0.25em] text-dblue">{t("webapps.exampleLabel").toUpperCase()}</p>
              <p className="mt-3 font-display text-xl font-extrabold tracking-tight">ESPAMANAGER.GR</p>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t("webapps.example")}</p>
              <Link to={link("/projects/espamanager")} data-testid="webapps-example-cta" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-dblue hover:gap-3 transition-all">
                {t("webapps.cta")} <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function EmailSection() {
  const { t, link } = useLang();
  return (
    <section data-testid="email-section" className="bg-white text-ink py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid items-center gap-14 lg:grid-cols-2">
        <Reveal>
          <p className="text-xs font-semibold tracking-[0.25em] text-dblue">{t("email.eyebrow")}</p>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">{t("email.title")}</h2>
          <p className="mt-5 text-base text-slate-600 leading-relaxed">{t("email.copy")}</p>
          <SectionCta to={link("/business-email")}>{t("email.cta")}</SectionCta>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="rounded-2xl border border-ink/10 bg-mist p-8">
            <div className="space-y-3">
              {t("email.examples").map((e) => (
                <div key={e} className="flex items-center gap-3 rounded-xl border border-ink/10 bg-white px-5 py-4">
                  <span className="h-2 w-2 rounded-full bg-gradient-to-r from-dblue to-electric" />
                  <span className="font-mono text-sm text-ink">{e}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {t("email.bullets").slice(0, 4).map((b) => (
                <span key={b} className="rounded-full bg-white border border-ink/10 px-3 py-1.5 text-xs text-slate-600">{b}</span>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function B2bSection() {
  const { t } = useLang();
  return (
    <section id="why-b2b" data-testid="b2b-section" className="bg-mist text-ink py-20 lg:py-28 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <p className="text-xs font-semibold tracking-[0.25em] text-dblue">{t("b2b.eyebrow")}</p>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">{t("b2b.title")}</h2>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">{t("b2b.copy")}</p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-10 flex flex-wrap gap-3">
            {t("b2b.highlights").map((h, i) => (
              <span key={h} className={`rounded-full px-5 py-2.5 text-xs font-bold tracking-[0.18em] ${i % 2 === 0 ? "bg-ink text-white" : "border border-ink/15 bg-white text-ink"}`}>
                {h}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FinalCta() {
  const { t } = useLang();
  return (
    <section id="quote" data-testid="final-cta" className="relative overflow-hidden py-20 lg:py-28">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0A1B32] via-ink to-[#15315C]" />
      <div className="absolute inset-0 bg-grid-dark" />
      <div className="pointer-events-none absolute -top-40 left-1/3 h-[420px] w-[420px] rounded-full bg-dblue/20 blur-[140px]" />
      <div className="pointer-events-none absolute -bottom-32 -right-24 h-[380px] w-[380px] rounded-full bg-electric/10 blur-[120px]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-14 lg:grid-cols-2 lg:items-start">
        <Reveal>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.12]">{t("finalCta.title")}</h2>
          <p className="mt-6 text-base sm:text-lg text-steel leading-relaxed">{t("finalCta.sub")}</p>
          <p className="mt-10 font-display text-lg font-bold text-gradient">«{t("tagline")}»</p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="rounded-2xl border border-white/10 bg-ink2/70 p-6 sm:p-8 backdrop-blur">
            <ContactForm />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Seo page="home" />
      <Hero />
      <Marquee />
      <ServiceCards />
      <FlowSection />
      <ProjectShowcase />
      <AdsSection />
      <WebSection />
      <AiSection />
      <CrmSection />
      <AiCrmSection />
      <WebappsSection />
      <EmailSection />
      <B2bSection />
      <PhotoBand />
      <EcosystemGraph />
      <UseCases />
      <Packages />
      <FinalCta />
    </>
  );
}
